import React, { useState, useEffect, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import {
  addModeleRecord,
  updateModeleRecord,
  uploadToStorage,
} from "../services/data";
import { validateTemplateAndConfig } from "../components/TemplateValidator";
import { evaluateFormula } from "../utils/formulas";
import FormulaHelper from "../components/FormulaHelper";
import { validateFormula } from "../utils/formulaValidation";

const toCSV = (v) => (Array.isArray(v) ? v.join(",") : v || "");
const fromCSV = (s) =>
  String(s || "")
    .split(/[;,\n\r]+/g)
    .map((t) => t.trim())
    .filter(Boolean);

const CONFIG_CACHE_PREFIX = "modeleConfigCache";
const cacheKeyForModele = (modeleId) =>
  `${CONFIG_CACHE_PREFIX}:${modeleId || "temp"}`;

const normalizeTimestamp = (value) => {
  if (!value) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof value === "object") {
    if (typeof value.toMillis === "function") {
      try {
        return value.toMillis();
      } catch {
        /* noop */
      }
    }
    const seconds = Number(value.seconds);
    if (Number.isFinite(seconds)) {
      const nanos = Number(value.nanoseconds) || 0;
      return seconds * 1000 + Math.round(nanos / 1e6);
    }
  }
  return null;
};

const readConfigCache = (modele) => {
  if (!modele?.id) return null;
  try {
    const raw = localStorage.getItem(cacheKeyForModele(modele.id));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      modele.configURL &&
      parsed &&
      parsed.configURL &&
      parsed.configURL !== modele.configURL
    ) {
      return null;
    }
    const currentTs = normalizeTimestamp(modele.updatedAt);
    if (
      currentTs &&
      parsed &&
      typeof parsed.updatedAt === "number" &&
      parsed.updatedAt !== currentTs
    ) {
      return null;
    }
    return parsed?.payload || null;
  } catch (err) {
    console.warn("[PlaceholdersConfig] readConfigCache:error", err);
    return null;
  }
};

const writeConfigCache = (modele, payload) => {
  if (!modele?.id) return;
  try {
    const entry = {
      payload,
      configURL: modele.configURL || null,
      updatedAt: normalizeTimestamp(modele.updatedAt) || Date.now(),
    };
    localStorage.setItem(cacheKeyForModele(modele.id), JSON.stringify(entry));
  } catch (err) {
    console.warn("[PlaceholdersConfig] writeConfigCache:error", err);
  }
};

// ---------- Excel helpers (mapping FR/EN) ----------
const HEADER_ALIASES = {
  key: [
    "key",
    "clé",
    "cle",
    "placeholder_key",
    "nom_champ",
    "nom",
    "nom_du_champ",
    "id",
    "identifiant",
    "code",
    "variable",
    "champ",
    "field",
    "name",
  ],
  label: ["label", "etiquette", "étiquette", "intitulé", "intitule"],
  type: ["type", "datatype", "data_type"],
  required: ["required", "obligatoire"],
  read_only: ["read_only", "lecture_seule", "lecture seule", "readonly"],
  section: ["section", "groupe", "categorie", "catégorie"],
  order: ["order", "ordre", "position"],
  placeholder: ["placeholder", "indice", "hint"],
  default: ["default", "defaut", "valeur_defaut", "valeur par defaut"],
  options: ["options", "choix", "liste"],
  visible_if: [
    "visible_if",
    "visible si",
    "condition_visibilite",
    "condition visibilité",
  ],
  template_if_checked: ["template_if_checked", "tpl_si_coché", "tpl_si_coche"],
  template_if_unchecked: [
    "template_if_unchecked",
    "tpl_si_décoché",
    "tpl_si_decoche",
  ],
  formula: ["formula", "formule"],
  max_length: ["max_length", "longueur_max", "maxlength"],
  regex_validation: [
    "regex_validation",
    "regex",
    "validation_regex",
    "pattern",
  ],
  mask: ["mask", "masque", "input_mask"],
  help_text: ["help_text", "aide", "texte_aide"],
  tooltip: ["tooltip", "info_bulle", "infobulle"],
  group_key: ["group_key", "cle_groupe", "clé_groupe"],
  repeat_section: ["repeat_section", "section_repetable", "répétable"],
  multi_select: ["multi_select", "multi", "multiple", "selection_multiple"],
  use_map: ["use_map", "usemap", "carte"],
  map_key: ["map_key", "map", "cle_carte", "clé_carte"],
  outputNameTemplate: [
    "outputnametemplate",
    "output_name_template",
    "nom_fichier_tpl",
  ],
};

const ALLOWED_TYPES = [
  "textarea",
  "text",
  "select",
  "checkbox",
  "number",
  "date",
  "image",
];

const TYPE_LABELS = {
  textarea: "Zone de texte",
  text: "Texte",
  select: "Sélection",
  checkbox: "Case à cocher",
  number: "Nombre",
  date: "Date",
  image: "Image",
};

const fixFieldType = (t) =>
  ALLOWED_TYPES.includes(String(t || "").toLowerCase())
    ? String(t).toLowerCase()
    : normalizeType(t);

const sanitizeToken = (s = "") =>
  String(s ?? "")
    .replace(/\uFEFF/g, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_{2,}/g, "_");

const normalizeHeaderKey = (hRaw = "") => {
  const h = sanitizeToken(hRaw);
  // Guard: if h is already a canonical header name, return it immediately
  // to prevent remapping (e.g. "placeholder" → "key" via alias collision)
  if (HEADER_ALIASES[h]) return h;
  for (const canon in HEADER_ALIASES) {
    const arr = HEADER_ALIASES[canon];
    if (arr.some((a) => sanitizeToken(a) === h)) return canon;
  }
  return h;
};

const coerceBool = (v) => {
  if (typeof v === "boolean") return v;
  const s = String(v || "")
    .trim()
    .toLowerCase();
  return ["1", "true", "vrai", "oui", "yes", "y"].includes(s);
};
const coerceInt = (v, def = undefined) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : def;
};
const normalizeType = (t) => {
  const s = String(t || "text")
    .trim()
    .toLowerCase();
  if (["textarea", "zone", "longtext"].includes(s)) return "textarea";
  if (["text", "texte", "string", "email", "mail"].includes(s)) return "text";
  if (["select", "liste", "dropdown", "combo"].includes(s)) return "select";
  if (["checkbox", "case", "bool", "boolean"].includes(s)) return "checkbox";
  if (["number", "num", "nombre", "numeric"].includes(s)) return "number";
  if (["date", "datetime", "jour"].includes(s)) return "date";
  if (["image", "photo", "pic"].includes(s)) return "image";
  return "text";
};

const HEADER_SENTINELS = new Set([
  "key",
  "placeholder",
  "label",
  "type",
  "section",
  "order",
  "required",
  "read_only",
  "options",
  "default",
  "formula",
  "mask",
  "visible_if",
]);

const looksLikeHeader = (cells = []) => {
  const canon = cells.map((c) => normalizeHeaderKey(String(c || "")));
  return canon.some((h) => HEADER_SENTINELS.has(h));
};

const sheetToObjectsNormalized = (ws) => {
  let rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
  if (rows.length) {
    const keys = Object.keys(rows[0]);
    const canon = keys.map((k) => normalizeHeaderKey(k));
    if (canon.some((h) => HEADER_SENTINELS.has(h))) {
      const map = keys.reduce((acc, k, i) => {
        acc[k] = canon[i];
        return acc;
      }, {});
      return rows.map((r) => {
        const obj = {};
        keys.forEach((k) => {
          obj[map[k]] = r[k];
        });
        return obj;
      });
    }
  }

  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const idx = aoa.findIndex(looksLikeHeader);
  if (idx !== -1) {
    const header = aoa[idx].map((h) => normalizeHeaderKey(h));
    const data = aoa.slice(idx + 1);
    const out = data.map((row) => {
      const obj = {};
      for (let i = 0; i < header.length; i++) obj[header[i]] = row[i] ?? "";
      return obj;
    });
    return out.filter((o) =>
      Object.values(o).some((v) => String(v).trim() !== "")
    );
  }

  return [];
};

const buildSections = (rows) => {
  const uniq = [
    ...new Set(rows.map((r) => (r.section || "").trim()).filter(Boolean)),
  ];
  if (!uniq.length) return [{ id: "general", label: "Général", order: 1 }];
  return uniq.map((name, i) => ({
    id: `sec_${i + 1}`,
    label: name,
    order: i + 1,
  }));
};

const rowToField = (row, index) => {
  const opts = toCSV(fromCSV(row.options));
  let key = String(row.key || row.placeholder || "").trim();
  if (!key && row.label) {
    key = sanitizeToken(String(row.label)).replace(/[^a-z0-9_]/g, "");
  }

  return {
    id: index + 1,
    key,
    label: String(row.label || key || "").trim(),
    type: normalizeType(row.type),
    required: coerceBool(row.required),
    read_only: coerceBool(row.read_only),
    placeholder: row.placeholder ?? "",
    default: row.default ?? "",
    options: opts,
    section: String(row.section || "Général").trim() || "Général",
    order: coerceInt(row.order, index + 1),
    advanced: {
      visible_if: row.visible_if ?? "",
      template_if_checked: row.template_if_checked ?? "",
      template_if_unchecked: row.template_if_unchecked ?? "",
      formula: row.formula ?? "",
      max_length: row.max_length ?? "",
      regex_validation: row.regex_validation ?? "",
      mask: row.mask ?? "",
      help_text: row.help_text ?? "",
      tooltip: row.tooltip ?? "",
      group_key: row.group_key ?? "",
      repeat_section: coerceBool(row.repeat_section),
      multi_select: coerceBool(row.multi_select),
      map_key:
        typeof row.map_key === "string"
          ? row.map_key.trim()
          : typeof row.use_map === "string"
          ? row.use_map.trim()
          : "",
    },
    showAdvanced: false,
  };
};

const makeMapRowId = (() => {
  let seq = 0;
  return () => `map_${Date.now()}_${seq++}`;
})();

const attachMapRowIds = (rows = []) =>
  rows.map((row) => ({ id: makeMapRowId(), ...row }));

const mapObjectToRows = (mapsObj = {}) => {
  const rows = [];
  Object.entries(mapsObj || {}).forEach(([mapKey, entries]) => {
    (Array.isArray(entries) ? entries : []).forEach((entry) => {
      rows.push({
        map_key: mapKey,
        map_label: entry.map_label ?? "",
        map_value:
          entry.map_value !== undefined && entry.map_value !== null
            ? String(entry.map_value)
            : "",
        order:
          entry.order === undefined || entry.order === null ? "" : entry.order,
        is_default: !!entry.is_default,
      });
    });
  });
  if (!rows.length) return [];
  return attachMapRowIds(
    rows.sort((a, b) => {
      const keyCmp = a.map_key.localeCompare(b.map_key);
      if (keyCmp !== 0) return keyCmp;
      const ao = Number(a.order ?? 0);
      const bo = Number(b.order ?? 0);
      const aoOk = Number.isFinite(ao);
      const boOk = Number.isFinite(bo);
      if (aoOk && boOk) return ao - bo;
      if (aoOk) return -1;
      if (boOk) return 1;
      return String(a.map_label).localeCompare(String(b.map_label));
    })
  );
};

const sanitizeMapRows = (rows = []) =>
  rows
    .map((row) => {
      const map_key = String(row.map_key || "").trim();
      const map_label = String(row.map_label || "").trim();
      const map_value =
        row.map_value !== undefined && row.map_value !== null
          ? String(row.map_value)
          : "";
      const orderRaw = row.order;
      let order = null;
      if (orderRaw === "" || orderRaw === null || orderRaw === undefined) {
        order = null;
      } else if (typeof orderRaw === "number") {
        order = Number.isFinite(orderRaw) ? orderRaw : null;
      } else {
        const parsed = Number(orderRaw);
        order = Number.isFinite(parsed) ? parsed : null;
      }
      return {
        ...row,
        map_key,
        map_label,
        map_value,
        order,
        is_default: !!row.is_default,
      };
    })
    .filter(
      (row) =>
        row.map_key ||
        row.map_label ||
        row.map_value ||
        row.order !== null ||
        row.is_default
    );

const serializeMapRows = (rows = []) => {
  const grouped = {};
  const normalized = sanitizeMapRows(rows);
  normalized.forEach((row) => {
    if (!row.map_key || !row.map_label || !row.map_value) return;
    const entry = {
      map_label: row.map_label,
      map_value: row.map_value,
      order: Number.isFinite(row.order) ? row.order : null,
      is_default: row.is_default,
    };
    (grouped[row.map_key] ||= []).push(entry);
  });
  Object.values(grouped).forEach((entries) => {
    entries.sort((a, b) => {
      const ao = Number.isFinite(a.order) ? a.order : Number.MAX_SAFE_INTEGER;
      const bo = Number.isFinite(b.order) ? b.order : Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return String(a.map_label).localeCompare(String(b.map_label));
    });
    entries.forEach((entry, idx) => {
      if (!Number.isFinite(entry.order)) entry.order = idx + 1;
    });
  });
  return grouped;
};

const PlaceholdersConfig = ({ user, modele, onBack, onSaved }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState("");
  const [sourceLabel, setSourceLabel] = useState("");
  const [fields, setFields] = useState([]);
  const [sections, setSections] = useState([
    { id: "general", label: "Général", order: 1 },
  ]);
  const [maps, setMaps] = useState([]);
  const excelInputRef = useRef(null);
  const jsonInputRef = useRef(null);
  const [meta, setMeta] = useState({
    outputNameTemplate: "facture-{{nom_client}}-{{date}}.docx",
  });
  const [showFormulaHelper, setShowFormulaHelper] = useState(false);
  const [currentFieldId, setCurrentFieldId] = useState(null);
  const [formulaValidation, setFormulaValidation] = useState({});

  const mapKeys = useMemo(() => {
    const out = new Set();
    sanitizeMapRows(maps).forEach((r) => {
      const k = String(r.map_key || "").trim();
      if (k) out.add(k);
    });
    return Array.from(out).sort((a, b) => a.localeCompare(b));
  }, [maps]);

  const [showExportPicker, setShowExportPicker] = useState(false);

  const applyConfigPayload = (payload, label) => {
    if (!payload) return false;
    const nextSections =
      Array.isArray(payload.sections) && payload.sections.length
        ? payload.sections
        : [{ id: "general", label: "General", order: 1 }];
    const normalizedFields = normalizeFieldsSections(
      payload.fields || [],
      nextSections
    );
    setMeta(
      payload.meta || {
        outputNameTemplate: "document-{{date}}.docx",
      }
    );
    setSections(nextSections);
    setFields(normalizedFields);
    setMaps(mapObjectToRows(payload.maps || {}));
    setSourceLabel(label);
    return true;
  };

  const buildConfigObject = () => ({
    meta,
    sections,
    fields,
    maps: serializeMapRows(maps),
    _source: sourceLabel,
  });

  const exportConfigAsJSON = () => {
    const config = buildConfigObject();
    const dataStr = JSON.stringify(config, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "configuration-placeholders.json";
    link.click();
  };

  const exportConfigAsExcel = () => {
    const wb = XLSX.utils.book_new();

    const sectionById = new Map(sections.map((s) => [s.id, s.label]));
    const fieldsRows = (fields || []).map((f) => ({
      key: f.key || "",
      label: f.label || "",
      type: f.type || "text",
      required: !!f.required,
      read_only: !!f.read_only,
      placeholder: f.placeholder || "",
      default: f.default || "",
      options: f?.advanced?.map_key ? "" : String(f.options || ""),
      section: sectionById.get(f.sectionId) || "",
      order: f.order ?? "",
      visible_if: f?.advanced?.visible_if || "",
      template_if_checked: f?.advanced?.template_if_checked || "",
      template_if_unchecked: f?.advanced?.template_if_unchecked || "",
      formula: f?.advanced?.formula || "",
      max_length: f?.advanced?.max_length ?? "",
      regex_validation: f?.advanced?.regex_validation || "",
      mask: f?.advanced?.mask || "",
      help_text: f?.advanced?.help_text || "",
      tooltip: f?.advanced?.tooltip || "",
      group_key: f?.advanced?.group_key || "",
      repeat_section: !!f?.advanced?.repeat_section,
      multi_select: !!f?.advanced?.multi_select,
      map_key: f?.advanced?.map_key || "",
    }));
    const wsFields = XLSX.utils.json_to_sheet(fieldsRows);
    XLSX.utils.book_append_sheet(wb, wsFields, "Fields");

    const wsSections = XLSX.utils.json_to_sheet(
      (sections || []).map((s) => ({
        id: s.id,
        label: s.label,
        order: s.order,
      }))
    );
    XLSX.utils.book_append_sheet(wb, wsSections, "Sections");

    const wsMeta = XLSX.utils.json_to_sheet([
      { key: "outputNameTemplate", value: meta?.outputNameTemplate || "" },
    ]);
    XLSX.utils.book_append_sheet(wb, wsMeta, "Meta");

    const wsMaps = XLSX.utils.json_to_sheet(
      sanitizeMapRows(maps).map((r) => ({
        map_key: r.map_key,
        map_label: r.map_label,
        map_value: r.map_value,
        order: r.order ?? "",
        is_default: !!r.is_default,
      }))
    );
    XLSX.utils.book_append_sheet(wb, wsMaps, "Maps");

    XLSX.writeFile(wb, "configuration-placeholders.xlsx");
  };

  const computeFieldsMetaFromState = (fields = [], sections = []) => {
    const sectionIdToLabel = new Map(sections.map((s) => [s.id, s.label]));
    const meta = {};
    (fields || []).forEach((f) => {
      if (!f?.key) return;
      meta[f.key] = {
        label: f.label || f.key,
        type: f.type || "text",
        required: !!f.required,
        read_only: !!f.read_only,
        placeholder: f.placeholder || "",
        default: f.default || "",
        options: String(f?.advanced?.map_key || "").trim()
          ? []
          : fromCSV(f.options),
        section: sectionIdToLabel.get(f.sectionId) || "Général",
        visible_if: f?.advanced?.visible_if || "",
        formula: f?.advanced?.formula || "",
        group_key: f?.advanced?.group_key || "",
        repeat_section: !!f?.advanced?.repeat_section,
        multi_select: !!f?.advanced?.multi_select,
        map_key: f?.advanced?.map_key || "",
      };
    });
    return meta;
  };

  const exportConfiguration = () => setShowExportPicker(true);

  const insertFormulaFunction = (syntax) => {
    if (currentFieldId) {
      const field = fields.find((f) => f.id === currentFieldId);
      if (field) {
        const newFormula = field.advanced.formula + syntax;
        updateAdvanced(currentFieldId, "formula", newFormula);
      }
    }
  };

  const toggleAdvanced = (fieldId) => {
    setFields(
      fields.map((field) =>
        field.id === fieldId
          ? { ...field, showAdvanced: !field.showAdvanced }
          : field
      )
    );
  };

  const updateField = (fieldId, key, value) => {
    setFields(
      fields.map((field) =>
        field.id === fieldId ? { ...field, [key]: value } : field
      )
    );
  };

  const updateAdvanced = (fieldId, key, value) => {
    setFields(
      fields.map((field) =>
        field.id === fieldId
          ? {
              ...field,
              advanced: { ...field.advanced, [key]: value },
            }
          : field
      )
    );
    if (key === "formula") {
      const validation = validateFormula(value);
      setFormulaValidation(validation);
    }
  };

  const addMapRow = () => {
    setMaps((prev) => [
      ...prev,
      {
        id: makeMapRowId(),
        map_key: "",
        map_label: "",
        map_value: "",
        order: prev.length + 1,
        is_default: false,
      },
    ]);
  };

  const updateMapRow = (rowId, key, value) => {
    setMaps((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [key]: value } : row))
    );
  };

  const deleteMapRow = (rowId) => {
    if (!window.confirm("Supprimer cette entree de map ?")) return;
    setMaps((prev) => prev.filter((row) => row.id !== rowId));
  };

  const addField = () => {
    const newField = {
      id: Date.now(),
      key: "",
      label: "",
      type: "text",
      required: false,
      read_only: false,
      placeholder: "",
      default: "",
      options: "",
      sectionId: sections[0]?.id || "general",
      order: fields.length + 1,
      advanced: {
        visible_if: "",
        template_if_checked: "",
        formula: "",
        max_length: "",
        regex_validation: "",
        mask: "",
        help_text: "",
        tooltip: "",
        template_if_unchecked: "",
        group_key: "",
        repeat_section: false,
        multi_select: false,
        map_key: "",
      },
      showAdvanced: false,
    };
    setFields([...fields, newField]);
  };

  const deleteField = (fieldId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce champ ?")) {
      setFields(fields.filter((field) => field.id !== fieldId));
    }
  };

  const addSection = () => {
    const newSection = {
      id: `section${sections.length + 1}`,
      label: "",
      order: sections.length + 1,
    };
    setSections([...sections, newSection]);
  };

  const deleteSection = (sectionId) => {
    const used = fields.some((f) => f.sectionId === sectionId);
    if (used) {
      alert(
        "Impossible de supprimer cette section car des champs y sont rattachés. Déplace les champs avant de supprimer."
      );
      return;
    }
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette section ?")) {
      setSections(sections.filter((section) => section.id !== sectionId));
    }
  };

  const updateSection = (sectionId, key, value) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId ? { ...section, [key]: value } : section
      )
    );
  };

  const updateMeta = (key, value) => {
    setMeta({ ...meta, [key]: value });
  };

  const saveConfiguration = async () => {
    if (isSaving) return;
    setSaveMessage("");

    console.log("[PlaceholdersConfig] saveConfiguration:start", {
      meta,
      sectionsCount: sections.length,
      fieldsCount: fields.length,
      mapsCount: maps.length,
      hasUser: !!user?.uid,
      hasModele: !!modele?.id,
    });

    const payload = buildConfigObject();
    const { ok, errors, warnings } = validateBeforeSave({
      sections,
      fields,
      maps,
    });
    if (!ok) {
      console.warn("[PlaceholdersConfig] validation:errors", errors);
      alert("Erreurs de validation:\n- " + errors.join("\n- "));
      return;
    }
    if (warnings.length) {
      console.warn("[PlaceholdersConfig] validation:warnings", warnings);
    }
    if (!user?.uid) {
      alert("Connecte-toi pour enregistrer la configuration.");
      return;
    }

    setIsSaving(true);

    try {
      let currentModele = modele || {};
      let modeleId = currentModele.id || null;
      const nextFieldsMeta = computeFieldsMetaFromState(fields, sections);

      if (!modeleId) {
        console.log("[PlaceholdersConfig] creating modele stub");
        const minimalPayload = {
          nom: currentModele.nom || "Modele sans nom",
          description: currentModele.description || "",
          dossier: currentModele.dossier || "Propositions",
          icon: currentModele.icon || "fas fa-file",
          fileURL: currentModele.fileURL || "",
          storagePath: currentModele.storagePath || "",
          docxMeta: currentModele.docxMeta || currentModele.docx_meta || null,
          configInline: payload,
          fields_meta: nextFieldsMeta,
          source: currentModele.source || "Personnalise",
          type: currentModele.type || "Contrat",
        };
        try {
          const created = await addModeleRecord(user.uid, minimalPayload);
          modeleId = created.id;
          currentModele = { ...currentModele, id: modeleId, ...minimalPayload };
          console.log("[PlaceholdersConfig] modele stub created", modeleId);
        } catch (err) {
          console.error("[PlaceholdersConfig] addModeleRecord:error", err);
          alert("Impossible de creer le modele avant la sauvegarde.");
          return;
        }
      }

      // construire le JSON à uploader
      const jsonBlob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      let storagePath = currentModele.configPath || null;
      let fileName = null;

      // si on a déjà un fichier de config, on réécrit dessus
      if (storagePath) {
        const parts = storagePath.split("/");
        fileName = parts[parts.length - 1] || "config.json";
      } else {
        // première fois : on génère un nom
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        fileName = modeleId + "-" + timestamp + ".json";
        storagePath = "modeles-config/" + fileName;
      }

      const jsonFile = new File([jsonBlob], fileName, {
        type: "application/json",
      });

      let uploadResult = null;
      try {
        uploadResult = await uploadToStorage({
          uid: user.uid,
          file: jsonFile,
          path: storagePath,
          contentType: "application/json",
        });
        console.log("[PlaceholdersConfig] upload:done", uploadResult);
      } catch (err) {
        console.error("[PlaceholdersConfig] upload:error", err);
        alert(
          "Impossible de televerser la configuration JSON dans le stockage."
        );
      }

      const updateBody = {
        configInline: payload,
        fields_meta: nextFieldsMeta,
      };

      if (uploadResult?.fileURL) {
        updateBody.configURL = uploadResult.fileURL;
        updateBody.configPath = uploadResult.storagePath;
      }

      await updateModeleRecord(user.uid, modeleId, updateBody);
      console.log("[PlaceholdersConfig] update:done", { modeleId });

      const updatedModele = {
        ...currentModele,
        ...updateBody,
        id: modeleId,
      };

      writeConfigCache(updatedModele, payload);
      if (uploadResult?.fileURL) {
        setSourceLabel("configURL");
      } else {
        setSourceLabel("configInline");
      }
      setSaveMessage("Configuration enregistrée avec succès.");
      onSaved?.(updatedModele);
    } catch (e) {
      console.error("saveConfiguration failed:", e);
      alert("Erreur lors de l'enregistrement de la configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  function validateBeforeSave({ sections, fields, maps }) {
    const errors = [];
    const warnings = [];

    const secIds = sections.map((s) => s.id);
    const dupSec = secIds.filter((v, i, a) => a.indexOf(v) !== i);
    if (dupSec.length)
      errors.push(
        `IDs de sections en double: ${[...new Set(dupSec)].join(", ")}`
      );

    const secIdSet = new Set(secIds);

    const keys = fields.map((f) => (f.key || "").trim());
    if (keys.some((k) => !k)) errors.push("Certains champs ont une clé vide.");
    const dupKeys = keys.filter((v, i, a) => v && a.indexOf(v) !== i);
    if (dupKeys.length)
      errors.push(
        `Clés de champs en double: ${[...new Set(dupKeys)].join(", ")}`
      );

    const badFk = fields.filter((f) => !secIdSet.has(f.sectionId));
    if (badFk.length)
      errors.push(
        `Champs rattachés à une section inexistante: ${badFk
          .map((f) => f.key)
          .join(", ")}`
      );

    const badSelect = fields.filter((f) => {
      const hasOptions = String(f.options || "").trim().length > 0;
      const hasMapKey = String(f?.advanced?.map_key || "").trim().length > 0;
      return f.type === "select" && !hasOptions && !hasMapKey;
    });
    if (badSelect.length) {
      warnings.push(
        `Champs select sans source d'options: ${badSelect
          .map((f) => f.key)
          .join(", ")}`
      );
    }

    const mapRows = sanitizeMapRows(maps);
    const validMapRows = mapRows.filter(
      (row) => row.map_key && row.map_label && row.map_value
    );
    if (mapRows.length) {
      const missingKey = mapRows.filter(
        (row) =>
          !row.map_key &&
          (row.map_label ||
            row.map_value ||
            row.order !== null ||
            row.is_default)
      );
      if (missingKey.length) {
        errors.push("Certaines entrées de Maps n'ont pas de map_key.");
      }

      const groupedMaps = validMapRows.reduce((acc, row) => {
        (acc[row.map_key] ||= []).push(row);
        return acc;
      }, {});

      Object.entries(groupedMaps).forEach(([mapKey, list]) => {
        const defaults = list.filter((row) => row.is_default);
        if (defaults.length > 1) {
          warnings.push(
            `Map "${mapKey}" a plusieurs valeurs par défaut (la première sera utilisée).`
          );
        }
      });

      const availableMapKeys = new Set(Object.keys(groupedMaps));
      fields.forEach((f) => {
        const mk = String(f?.advanced?.map_key || "").trim();
        if (!mk) return;
        if (!availableMapKeys.has(mk)) {
          errors.push(
            `Champ "${f.key}" référence une carte inconnue: "${mk}".`
          );
        }
        if (f.type !== "select") {
          warnings.push(
            `Champ "${f.key}": une carte est sélectionnée mais le type n'est pas "select".`
          );
        }
        if (String(f.options || "").trim()) {
          warnings.push(
            `Champ "${f.key}": "options" sera ignoré car une carte est sélectionnée.`
          );
        }
      });
    }

    return { ok: errors.length === 0, errors, warnings };
  }

  function buildSkeletonFromDocxMeta(docxMeta) {
    if (!docxMeta || !Array.isArray(docxMeta.keys)) return null;

    const sectionNames = (docxMeta.sectionsOpen || []).map((s) =>
      s.replace(/^#/, "").trim()
    );
    const baseSections = sectionNames.length
      ? sectionNames.map((n, i) => ({
          id: `sec_${i + 1}`,
          label: n || `Section ${i + 1}`,
          order: i + 1,
        }))
      : [{ id: "general", label: "Général", order: 1 }];

    const imgSet = new Set(docxMeta.percentTagKeys || []);

    const fields = (docxMeta.keys || []).map((k, i) => ({
      id: i + 1,
      key: k,
      label: k,
      type: imgSet.has(k) ? "image" : "text",
      required: false,
      read_only: false,
      placeholder: "",
      default: "",
      options: "",
      sectionId: baseSections[0]?.id || "general",
      order: i + 1,
      advanced: {
        visible_if: "",
        template_if_checked: "",
        formula: "",
        max_length: "",
        regex_validation: "",
        mask: "",
        help_text: "",
        tooltip: "",
        template_if_unchecked: "",
        group_key: "",
        repeat_section: false,
        multi_select: false,
        map_key: "",
      },
      showAdvanced: false,
    }));
    return {
      meta: { outputNameTemplate: "document-{{date}}.docx" },
      sections: baseSections,
      fields,
    };
  }

  async function hydrateFromDocxFile(file) {
    if (!file) throw new Error("Fichier DOCX absent");
    const res = await validateTemplateAndConfig(file, null);
    if (!res?.docx?.keys?.length) {
      throw new Error("DOCX analyse mais aucune cle trouvee.");
    }
    return buildSkeletonFromDocxMeta(res.docx);
  }

  async function hydrateFromDocxURL(fileURL) {
    const resp = await fetch(fileURL);
    if (!resp.ok)
      throw new Error(`Échec de téléchargement du DOCX (${resp.status})`);
    const blob = await resp.blob();
    const file = new File([blob], "modele.docx", {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
    const res = await validateTemplateAndConfig(file, null);
    if (!res?.docx?.keys?.length) {
      throw new Error("DOCX parsé mais aucune clé trouvée.");
    }
    const skeleton = buildSkeletonFromDocxMeta(res.docx);
    return skeleton;
  }

  function pickFieldsSheet(workbook) {
    const wantedExact = [
      "Fields",
      "FIELDS",
      "Champs",
      "Placeholders",
      "Sheet1",
      "Feuil1",
      "Feuille1",
    ];
    const names = workbook.SheetNames;
    const exact = names.find((n) =>
      wantedExact.some((w) => w.toLowerCase() === n.trim().toLowerCase())
    );
    if (exact) return workbook.Sheets[exact];

    const fuzzy = names.find(
      (n) =>
        /field|champ|placehold/i.test(n) &&
        !/meta|section|readme|array|map/i.test(n)
    );
    if (fuzzy) return workbook.Sheets[fuzzy];

    const first = names.find((n) => {
      if (/meta|section|readme|array|map/i.test(n)) return false;
      const arr = XLSX.utils.sheet_to_json(workbook.Sheets[n], { defval: "" });
      return arr && arr.length;
    });
    return first ? workbook.Sheets[first] : null;
  }

  function readSectionsSheet(workbook) {
    const names = ["Sections", "SECTIONS"];
    const name = names.find((n) => workbook.Sheets[n]);
    if (!name) return null;
    const rows = sheetToObjectsNormalized(workbook.Sheets[name]);
    const norm = rows
      .map((r, i) => {
        // Try multiple sources for ID (r.key because "id" header is normalized to "key")
        const rawId = r.id || r.key || r.section_id || r.sectionId;
        return {
          id: (rawId && String(rawId).trim()) || `sec_${i + 1}`,
          label: (r.label && String(r.label).trim()) || `Section ${i + 1}`,
          order: coerceInt(r.order, i + 1),
        };
      })
      .filter((s) => s.label);
    return norm.length ? norm : null;
  }

  function readMetaSheet(workbook) {
    const candidates = [
      "Meta",
      "META",
      "meta",
      "Configuration",
      "Config",
      "Configuration Meta",
      "Parametres",
      "Paramètres",
      "Settings",
    ];
    const found = workbook.SheetNames.find((n) =>
      candidates.some((c) => c.toLowerCase() === n.trim().toLowerCase())
    );
    if (!found) return null;

    const ws = workbook.Sheets[found];
    const rows = sheetToObjectsNormalized(ws);
    if (!rows.length) return null;

    const first = rows[0] || {};
    if (first.outputNameTemplate) {
      return { outputNameTemplate: String(first.outputNameTemplate) };
    }

    const out = {};
    for (const r of rows) {
      const kRaw = String(r.key || "").trim();
      if (!kRaw) continue;
      const k = kRaw
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\s+/g, "");
      const v = r.value ?? r.val ?? r.valeur ?? "";
      if (
        [
          "outputnametemplate",
          "output_name_template",
          "nom_fichier_tpl",
        ].includes(k)
      ) {
        out.outputNameTemplate = String(v);
      }
    }
    return Object.keys(out).length ? out : null;
  }

  function readMapsSheet(workbook) {
    const sheetName = workbook.SheetNames.find(
      (n) => n.trim().toLowerCase() === "maps"
    );
    if (!sheetName) return [];
    const rows = sheetToObjectsNormalized(workbook.Sheets[sheetName]);
    if (!rows.length) return [];
    return rows
      .map((r) => {
        const map_key = String(r.map_key || r.key || "").trim();
        const map_label = String(r.map_label || r.label || "").trim();
        const map_value =
          r.map_value !== undefined && r.map_value !== null
            ? String(r.map_value)
            : "";
        const rawOrder = coerceInt(r.order, null);
        return {
          map_key,
          map_label,
          map_value,
          order: rawOrder ?? "",
          is_default: coerceBool(r.is_default),
        };
      })
      .filter(
        (row) =>
          row.map_key ||
          row.map_label ||
          row.map_value ||
          row.order !== "" ||
          row.is_default
      );
  }

  const handleJsonFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const parsed = JSON.parse(text);

        if (!parsed || typeof parsed !== "object") {
          throw new Error("Le contenu JSON doit être un objet.");
        }
        if (!Array.isArray(parsed.fields) || !Array.isArray(parsed.sections)) {
          throw new Error(
            "Structure JSON invalide: 'fields' et 'sections' sont requis."
          );
        }

        if (applyConfigPayload(parsed, "json_import")) {
          writeConfigCache(modele, parsed);
          alert("Configuration JSON importée avec succès.");
        }
      } catch (err) {
        console.error("handleJsonFile error:", err);
        alert(`Échec import JSON: ${err.message || err}`);
      }
    };
    reader.onerror = () => {
      alert("Erreur de lecture du fichier.");
    };
    reader.readAsText(file);
  };

  async function handleExcelFile(file) {
    try {
      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab, { type: "array", cellDates: true, raw: false });

      const wsFields = pickFieldsSheet(wb);
      if (!wsFields) {
        alert("Excel: aucune feuille 'Fields' (ou équivalent) trouvée.");
        return;
      }

      const rows = sheetToObjectsNormalized(wsFields);
      if (!rows.length) {
        console.warn(
          "[Excel] Aucune ligne parsée dans Fields. Vérifie qu'une ligne contient les entêtes (key, label, type, ...)."
        );
        alert(
          "Excel: je n'ai pas trouvé la ligne d'entêtes (ex: 'key', 'label', 'type')."
        );
        return;
      }

      const headerNames = Object.keys(rows[0] || {});
      const hasKeyCol = headerNames.includes("key");

      if (!hasKeyCol) {
        const hasPlaceholder = headerNames.includes("placeholder");
        if (hasPlaceholder) {
          rows.forEach((r) => {
            if (!r.key && r.placeholder) r.key = r.placeholder;
          });
        } else {
          rows.forEach((r) => {
            if (!r.key && r.label) {
              r.key = sanitizeToken(String(r.label)).replace(/[^a-z0-9_]/g, "");
            }
          });
        }
      }
      const rawFields = rows.map((r, i) => rowToField(r, i));

      const dupKeys = Object.entries(
        rawFields.reduce((acc, f) => {
          acc[f.key] = (acc[f.key] || 0) + 1;
          return acc;
        }, {})
      )
        .filter(([, count]) => count > 1)
        .map(([k]) => k)
        .filter(Boolean);

      if (dupKeys.length) {
        console.warn("Clés dupliquées:", dupKeys);
        alert(`Attention: clés dupliquées dans l'Excel: ${dupKeys.join(", ")}`);
      }

      const fieldsClean = rawFields.filter((f) => f.key);

      const sectionsFromSheet = readSectionsSheet(wb);
      const sectionsAuto = buildSections(fieldsClean);
      const sectionsFinal = sectionsFromSheet || sectionsAuto;

      const metaSheet = readMetaSheet(wb);
      const metaFinal = {
        outputNameTemplate:
          metaSheet?.outputNameTemplate || "document-{{date}}.docx",
      };

      setMeta(metaFinal);
      setSections(sectionsFinal);
      const label2id = new Map(
        sectionsFinal.map((s) => [String(s.label || "").trim(), s.id])
      );
      const withSecId = fieldsClean.map((f, i) => ({
        ...f,
        id: i + 1,
        sectionId:
          label2id.get(String(f.section || "").trim()) || sectionsFinal[0].id,
        type: fixFieldType(f.type),
      }));
      withSecId.forEach((f) => {
        delete f.section;
      });
      setFields(withSecId);
      const mapsRows = readMapsSheet(wb);
      setMaps(attachMapRowIds(mapsRows));
      setSourceLabel("excel");

      alert(
        `Import Excel OK (${fieldsClean.length} champ(s), ${sectionsFinal.length} section(s), ${mapsRows.length} entree(s) de map)`
      );
    } catch (e) {
      console.error("handleExcelFile error:", e);
      alert(`Échec import Excel: ${e?.message || e}`);
    }
  }

  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        if (!modele) {
          setMeta({ outputNameTemplate: "document-{{date}}.docx" });
          setSections([{ id: "general", label: "General", order: 1 }]);
          setFields([]);
          setMaps([]);
          setSourceLabel("empty");
          return;
        }

        const cachedPayload = readConfigCache(modele);

        if (modele.configURL) {
          try {
            const response = await fetch(modele.configURL, {
              cache: "no-store",
            });
            if (!response.ok) {
              throw new Error("HTTP " + response.status);
            }
            const data = await response.json();
            if (applyConfigPayload(data, "configURL")) {
              writeConfigCache(modele, data);
              return;
            }
          } catch (err) {
            console.warn("[PlaceholdersConfig] configURL load failed", err);
            if (cachedPayload) {
              applyConfigPayload(cachedPayload, "configURL(cache)");
              return;
            }
          }
        } else if (cachedPayload) {
          if (applyConfigPayload(cachedPayload, "configURL(cache)")) {
            return;
          }
        }

        if (modele.configInline) {
          if (applyConfigPayload(modele.configInline, "configInline")) {
            return;
          }
        }

        const fm = modele.fields_meta || {};
        const fmKeys = Object.keys(fm);
        if (fmKeys.length) {
          setSections([{ id: "general", label: "General", order: 1 }]);
          setFields(
            fmKeys.map((k, i) => {
              const f = fm[k] || {};
              return {
                id: i + 1,
                key: k,
                label: f.label || k,
                type: fixFieldType(f.type || "text"),
                required: !!f.required,
                read_only: !!f.read_only,
                placeholder: f.placeholder || "",
                default: f.default || "",
                options: toCSV(f.options),
                sectionId: "general",
                order: i + 1,
                advanced: {
                  visible_if: f.visible_if || "",
                  template_if_checked: "",
                  formula: f.formula || "",
                  max_length: f.max_length ?? "",
                  regex_validation: f.regex_validation || "",
                  mask: f.mask || "",
                  help_text: f.help_text || "",
                  tooltip: f.tooltip || "",
                  template_if_unchecked: "",
                  group_key: f.group_key || "",
                  repeat_section: !!f.repeat_section,
                  multi_select: !!f.multi_select,
                  map_key:
                    typeof f?.map_key === "string"
                      ? f.map_key
                      : typeof f?.use_map === "string"
                      ? f.use_map
                      : "",
                },
                showAdvanced: false,
              };
            })
          );
          setMaps([]);
          setMeta({ outputNameTemplate: "document-{{date}}.docx" });
          setSourceLabel("fields_meta");
          return;
        }

        const inlineDocx = modele.docxMeta || modele.docx_meta;
        if (inlineDocx?.keys?.length) {
          const skeleton = buildSkeletonFromDocxMeta(inlineDocx);
          if (skeleton) {
            setMeta(skeleton.meta);
            setSections(skeleton.sections);
            setFields(skeleton.fields || []);
            setMaps(mapObjectToRows(skeleton.maps || {}));
            setSourceLabel("docxMeta");
            return;
          }
        }

        if (modele.__localTemplateFile) {
          try {
            const skeleton = await hydrateFromDocxFile(
              modele.__localTemplateFile
            );
            setMeta(skeleton.meta);
            setSections(skeleton.sections);
            setFields(skeleton.fields || []);
            setMaps(mapObjectToRows(skeleton.maps || {}));
            setSourceLabel("docx(local)");
            return;
          } catch (err) {
            console.warn("[PlaceholdersConfig] local DOCX parse failed", err);
          }
        }

        if (modele.fileURL) {
          try {
            const skeleton = await hydrateFromDocxURL(modele.fileURL);
            setMeta(skeleton.meta);
            setSections(skeleton.sections);
            setFields(skeleton.fields || []);
            setMaps(mapObjectToRows(skeleton.maps || {}));
            setSourceLabel("docx(url)");
            return;
          } catch (err) {
            console.warn("Parse DOCX via URL echoue:", err?.message || err);
          }
        }

        setMeta({ outputNameTemplate: "document-{{date}}.docx" });
        setSections([{ id: "general", label: "General", order: 1 }]);
        setFields([]);
        setMaps([]);
        setSourceLabel("empty");
      } catch (error) {
        console.error("Erreur lors du chargement de la configuration:", error);
        alert("Erreur lors du chargement de la configuration. Veuillez reessayer.");
        setMeta({ outputNameTemplate: "document-{{date}}.docx" });
        setSections([{ id: "general", label: "General", order: 1 }]);
        setFields([]);
        setMaps([]);
        setSourceLabel("error");
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadConfiguration();
  }, [
    modele?.id,
    modele?.configURL,
    modele?.updatedAt,
    modele?.configInline,
    modele?.fields_meta,
    modele?.docxMeta,
    modele?.docx_meta,
    modele?.fileURL,
  ]);

  function normalizeFieldsSections(inFields = [], inSections = []) {
    const label2id = new Map(
      inSections.map((s) => [String(s.label || "").trim(), s.id])
    );

    const absorbIntoAdvanced = (adv, src, dstKey, ...srcKeys) => {
      if (
        adv[dstKey] !== undefined &&
        adv[dstKey] !== null &&
        String(adv[dstKey]).trim() !== ""
      )
        return;

      for (const k of srcKeys) {
        const v = src[k];
        if (v === undefined || v === null) continue;
        if (typeof v === "string" && v.trim() === "") continue;
        adv[dstKey] = v;
        return;
      }
    };

    return (inFields || []).map((f, i) => {
      const hasSectionId = !!f.sectionId;
      const next = { id: f.id ?? i + 1, ...f };

      // sectionId fallback depuis legacy "section"
      if (!hasSectionId) {
        const label = String(f.section || "").trim();
        const secId = label2id.get(label) || inSections[0]?.id || "general";
        delete next.section;
        next.sectionId = secId;
      }

      // type + bool
      next.type = fixFieldType(next.type || "text");
      next.required = coerceBool(next.required);

      // readOnly (camelCase) -> read_only
      if (next.read_only === undefined && next.readOnly !== undefined) {
        next.read_only = coerceBool(next.readOnly);
      } else if (next.read_only !== undefined) {
        next.read_only = coerceBool(next.read_only);
      }
      delete next.readOnly;

      // options array (JSON) -> string (UI)
      next.options = toCSV(next.options);

      // merge legacy root-level -> advanced
      const adv =
        next.advanced && typeof next.advanced === "object" ? { ...next.advanced } : {};

      absorbIntoAdvanced(adv, next, "visible_if", "visible_if", "visibleIf");
      absorbIntoAdvanced(adv, next, "template_if_checked", "template_if_checked", "templateIfChecked");
      absorbIntoAdvanced(adv, next, "template_if_unchecked", "template_if_unchecked", "templateIfUnchecked");
      absorbIntoAdvanced(adv, next, "formula", "formula");
      absorbIntoAdvanced(adv, next, "max_length", "max_length", "maxLength");
      absorbIntoAdvanced(adv, next, "regex_validation", "regex_validation", "regexValidation", "regex");
      absorbIntoAdvanced(adv, next, "mask", "mask");
      absorbIntoAdvanced(adv, next, "help_text", "help_text", "helpText");
      absorbIntoAdvanced(adv, next, "tooltip", "tooltip");
      absorbIntoAdvanced(adv, next, "group_key", "group_key", "groupKey");
      absorbIntoAdvanced(adv, next, "repeat_section", "repeat_section", "repeatSection");
      absorbIntoAdvanced(adv, next, "multi_select", "multi_select", "multiSelect");
      absorbIntoAdvanced(adv, next, "map_key", "map_key", "use_map", "useMap", "mapKey");

      // normalize advanced
      next.advanced = {
        visible_if: String(adv.visible_if ?? ""),
        template_if_checked: String(adv.template_if_checked ?? ""),
        template_if_unchecked: String(adv.template_if_unchecked ?? ""),
        formula: String(adv.formula ?? ""),
        max_length:
          adv.max_length === undefined || adv.max_length === null ? "" : String(adv.max_length),
        regex_validation: String(adv.regex_validation ?? ""),
        mask: String(adv.mask ?? ""),
        help_text: String(adv.help_text ?? ""),
        tooltip: String(adv.tooltip ?? ""),
        group_key: String(adv.group_key ?? ""),
        repeat_section: coerceBool(adv.repeat_section),
        multi_select: coerceBool(adv.multi_select),
        map_key: String(adv.map_key ?? ""),
      };

      // cleanup legacy root keys
      delete next.visible_if;
      delete next.visibleIf;
      delete next.template_if_checked;
      delete next.templateIfChecked;
      delete next.template_if_unchecked;
      delete next.templateIfUnchecked;
      delete next.formula;
      delete next.max_length;
      delete next.maxLength;
      delete next.regex_validation;
      delete next.regexValidation;
      delete next.regex;
      delete next.help_text;
      delete next.helpText;
      delete next.repeat_section;
      delete next.repeatSection;
      delete next.multi_select;
      delete next.multiSelect;
      delete next.map_key;
      delete next.mapKey;
      delete next.use_map;
      delete next.useMap;
      delete next.group_key;
      delete next.groupKey;
      delete next.mask;
      delete next.tooltip;

      return next;
    });
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div className="alert-info" style={{ marginBottom: 8 }}>
          {isInitialLoading
            ? "Chargement de la configuration en cours..."
            : `Dernière tentative : ${new Date().toLocaleString()}`}
        </div>
        {sourceLabel && (
          <div className="alert-warning" style={{ marginTop: 8 }}>
            Source config : <strong>{sourceLabel}</strong>
            {(sourceLabel === "docxMeta" ||
              (sourceLabel !== "configInline" && modele?.fileURL)) && (
              <button
                className="btn btn-outline"
                style={{ marginLeft: 12 }}
                onClick={async () => {
                  if (
                    !window.confirm(
                      "Reparser depuis le DOCX va régénérer les sections/champs détectés. Continuer ?"
                    )
                  ) {
                    return;
                  }
                  try {
                    const sk = modele?.fileURL
                      ? await hydrateFromDocxURL(modele.fileURL)
                      : buildSkeletonFromDocxMeta(modele.docxMeta || {});
                    if (sk) {
                      setMeta(sk.meta);
                      setSections(sk.sections);
                      setFields(sk.fields);
                      setMaps(mapObjectToRows(sk.maps || {}));
                      setSourceLabel(
                        modele?.fileURL ? "docx(url)" : "docxMeta"
                      );
                      alert("DOCX re-parsé et configuration régénérée.");
                    }
                  } catch (e) {
                    alert(`Échec du re-parse DOCX: ${e?.message || e}`);
                  }
                }}
                title="Reparser le modèle Word pour régénérer la liste des clés/sections"
              >
                <i className="fas fa-sync-alt"></i> Reparser depuis DOCX
              </button>
            )}
          </div>
        )}

        {saveMessage && (
          <div className="alert-info" style={{ marginTop: 8 }}>
            {saveMessage}
          </div>
        )}

        <h1 className="page-title">
          Configuration des Placeholders {modele?.nom ? `- ${modele.nom}` : ""}
        </h1>
        <div className="page-actions">
          <input
            ref={jsonInputRef}
            type="file"
            accept=".json"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleJsonFile(file);
              e.currentTarget.value = "";
            }}
          />
          <button
            className="btn btn-outline"
            onClick={() => jsonInputRef.current?.click()}
            disabled={isSaving}
          >
            <i className="fas fa-file-code"></i> Importer JSON
          </button>
          <input
            ref={excelInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleExcelFile(file);
              e.currentTarget.value = "";
            }}
          />
          <button
            className="btn btn-outline"
            onClick={() => excelInputRef.current?.click()}
            disabled={isSaving}
          >
            <i className="fas fa-file-excel"></i> Importer Excel
          </button>
          <button
            className="btn btn-outline"
            onClick={exportConfiguration}
            disabled={isSaving}
          >
            <i className="fas fa-download"></i> Exporter
          </button>
          <button
            className="btn btn-primary"
            disabled={isSaving}
            onClick={() => {
              console.log("[PlaceholdersConfig] CLICK Enregistrer");
              saveConfiguration();
            }}
          >
            <i className="fas fa-save"></i>{" "}
            {isSaving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>

      <div className="config-section">
        <div className="config-header">
          <h2 className="config-title">
            <i className="fas fa-info-circle"></i> Configuration Meta
          </h2>
        </div>
        <div className="config-body">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Clé</label>
              <input
                type="text"
                className="form-input"
                value="outputNameTemplate"
                readOnly
              />
            </div>
            <div className="form-group">
              <label className="form-label">Valeur</label>
              <input
                type="text"
                className="form-input"
                value={meta.outputNameTemplate}
                onChange={(e) =>
                  updateMeta("outputNameTemplate", e.target.value)
                }
                placeholder="Template du nom de fichier"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="config-section">
        <div className="config-header">
          <h2 className="config-title">
            <i className="fas fa-layer-group"></i> Sections
          </h2>
          <button className="btn btn-primary" onClick={addSection}>
            <i className="fas fa-plus"></i> Ajouter
          </button>
        </div>
        <div className="config-body">
          <div className="table-container">
            <table className="placeholders-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Label</th>
                  <th>Ordre</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((section) => (
                  <tr key={section.id}>
                    <td>
                      <input
                        type="text"
                        className="form-input"
                        value={section.id}
                        readOnly
                        title="L'identifiant de section est utilisé comme clé technique et ne doit pas être modifié."
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-input"
                        value={section.label}
                        onChange={(e) =>
                          updateSection(section.id, "label", e.target.value)
                        }
                        placeholder="Nom de la section"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-input"
                        value={section.order}
                        style={{ minWidth: 120 }}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          updateSection(
                            section.id,
                            "order",
                            Number.isFinite(n) ? n : 0
                          );
                        }}
                      />
                    </td>
                    <td className="actions">
                      <div
                        className="action-btn"
                        title="Supprimer"
                        onClick={() => deleteSection(section.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="config-section">
        <div className="config-header">
          <h2 className="config-title">
            <i className="fas fa-list-alt"></i> Champs
          </h2>
          <button className="btn btn-primary" onClick={addField}>
            <i className="fas fa-plus"></i> Ajouter
          </button>
        </div>
        <div className="config-body">
          <div className="table-container">
            <table className="placeholders-table">
              <thead>
                <tr>
                  <th>Clé</th>
                  <th>Label</th>
                  <th>Type</th>
                  <th>Requis</th>
                  <th>Lecture seule</th>
                  <th>Section</th>
                  <th>Ordre</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field) => (
                  <React.Fragment key={field.id}>
                    <tr>
                      <td>
                        <input
                          type="text"
                          className="form-input"
                          value={field.key}
                          onChange={(e) =>
                            updateField(field.id, "key", e.target.value)
                          }
                          placeholder="nom_champ"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-input"
                          value={field.label}
                          onChange={(e) =>
                            updateField(field.id, "label", e.target.value)
                          }
                          placeholder="Label du champ"
                        />
                      </td>
                      <td>
                        <select
                          className="form-select"
                          value={field.type}
                          onChange={(e) =>
                            updateField(field.id, "type", e.target.value)
                          }
                        >
                          {ALLOWED_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {TYPE_LABELS[t]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="form-select"
                          value={field.required}
                          onChange={(e) =>
                            updateField(
                              field.id,
                              "required",
                              e.target.value === "true"
                            )
                          }
                        >
                          <option value={true}>Oui</option>
                          <option value={false}>Non</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className="form-select"
                          value={field.read_only}
                          onChange={(e) =>
                            updateField(
                              field.id,
                              "read_only",
                              e.target.value === "true"
                            )
                          }
                        >
                          <option value={true}>Oui</option>
                          <option value={false}>Non</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className="form-select"
                          value={field.sectionId}
                          onChange={(e) =>
                            updateField(field.id, "sectionId", e.target.value)
                          }
                        >
                          {sections.map((section) => (
                            <option key={section.id} value={section.id}>
                              {section.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-input"
                          value={field.order}
                          style={{ minWidth: 120 }}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            updateField(
                              field.id,
                              "order",
                              Number.isFinite(n) ? n : 0
                            );
                          }}
                        />
                      </td>
                      <td className="actions">
                        <div
                          className="action-btn"
                          title="Supprimer"
                          onClick={() => deleteField(field.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </div>
                        <button
                          className={`toggle-advanced ${
                            field.showAdvanced ? "active" : ""
                          }`}
                          onClick={() => toggleAdvanced(field.id)}
                        >
                          <i className="fas fa-cog"></i> Avancé
                          <i className="fas fa-chevron-down"></i>
                        </button>
                      </td>
                    </tr>

                    {field.showAdvanced && (
                      <tr className="advanced-row">
                        <td colSpan="8">
                          <div className="advanced-options show">
                            <h3>
                              <i className="fas fa-cogs"></i>
                              Options Avancées - {field.key || "Nouveau champ"}
                            </h3>
                            <div className="form-grid">
                              <div className="form-group">
                                <label className="form-label">Visible si</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={field.advanced?.visible_if}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "visible_if",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Condition de visibilité"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Options</label>
                                <textarea
                                  className="form-input"
                                  rows={3}
                                  value={toCSV(field.options)}
                                  onChange={(e) => updateField(field.id, "options", e.target.value)}
                                  placeholder="Option 1, Option 2 (séparées par virgule, ; ou retour à la ligne)"
                                  disabled={!!field.advanced?.map_key}
                                />
                                {field.advanced?.map_key && (
                                  <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                                    Options désactivées car ce champ utilise une Map ({field.advanced.map_key})
                                  </div>
                                )}
                              </div>
                              <div className="form-group">
                                <label className="form-label">
                                  Template si coché
                                </label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={field.advanced?.template_if_checked}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "template_if_checked",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Template lorsque coché"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Formule</label>
                                <div style={{ display: "flex", gap: "8px" }}>
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={field.advanced?.formula}
                                    onChange={(e) =>
                                      updateAdvanced(
                                        field.id,
                                        "formula",
                                        e.target.value
                                      )
                                    }
                                    onFocus={() => setCurrentFieldId(field.id)}
                                    placeholder="Formule de calcul"
                                    style={{ flex: 1 }}
                                  />
                                  <button
                                    className="btn btn-outline btn-sm"
                                    onClick={() => setShowFormulaHelper(true)}
                                    title="Ouvrir l'aide aux formules"
                                  >
                                    <i className="fas fa-calculator"></i>
                                  </button>
                                </div>
                                {formulaValidation?.error &&
                                  currentFieldId === field.id && (
                                    <div className="form-hint">
                                      {formulaValidation.error}
                                    </div>
                                  )}
                              </div>
                              <div className="form-group">
                                <label className="form-label">
                                  Longueur max
                                </label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={field.advanced?.max_length}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "max_length",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Longueur maximale"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">
                                  Validation regex
                                </label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={field.advanced?.regex_validation}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "regex_validation",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Expression régulière"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Masque</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={field.advanced?.mask}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "mask",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Masque de saisie"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">
                                  Texte d'aide
                                </label>
                                <textarea
                                  className="form-textarea"
                                  value={field.advanced?.help_text}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "help_text",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Texte d'aide"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">Info-bulle</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={field.advanced?.tooltip}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "tooltip",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Texte de l'info-bulle"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">
                                  Template si décoché
                                </label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={field.advanced?.template_if_unchecked}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "template_if_unchecked",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Template lorsque décoché"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">
                                  Clé de groupe
                                </label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={field.advanced?.group_key}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "group_key",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Clé du groupe"
                                />
                              </div>
                              <div className="form-group">
                                <label className="form-label">
                                  Section répétable
                                </label>
                                <select
                                  className="form-select"
                                  value={field.advanced?.repeat_section}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "repeat_section",
                                      e.target.value === "true"
                                    )
                                  }
                                >
                                  <option value={false}>Non</option>
                                  <option value={true}>Oui</option>
                                </select>
                              </div>
                              <div className="form-group">
                                <label className="form-label">
                                  Sélection multiple
                                </label>
                                <select
                                  className="form-select"
                                  value={field.advanced?.multi_select}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "multi_select",
                                      e.target.value === "true"
                                    )
                                  }
                                >
                                  <option value={false}>Non</option>
                                  <option value={true}>Oui</option>
                                </select>
                              </div>
                              <div className="form-group">
                                <label className="form-label">
                                  Utiliser la carte
                                </label>
                                <select
                                  className="form-select"
                                  value={field.advanced?.map_key || ""}
                                  onChange={(e) =>
                                    updateAdvanced(
                                      field.id,
                                      "map_key",
                                      e.target.value
                                    )
                                  }
                                  disabled={
                                    mapKeys.length === 0 ||
                                    field.type !== "select"
                                  }
                                  title={
                                    mapKeys.length === 0
                                      ? "Aucune map définie dans la section Maps"
                                      : ""
                                  }
                                >
                                  <option value="">
                                    {mapKeys.length
                                      ? "- Aucune -"
                                      : "Aucune map définie"}
                                  </option>
                                  {mapKeys.map((k) => (
                                    <option key={k} value={k}>
                                      {k}
                                    </option>
                                  ))}
                                </select>
                                {field.type !== "select" && (
                                  <small className="form-hint">
                                    Disponible pour les champs de type "
                                    Sélection ".
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="config-section">
        <div className="config-header">
          <h2 className="config-title">
            <i className="fas fa-map"></i> Maps
          </h2>
          <button className="btn btn-primary" onClick={addMapRow}>
            <i className="fas fa-plus"></i> Ajouter
          </button>
        </div>
        <div className="config-body">
          <div className="table-container">
            <table className="placeholders-table">
              <thead>
                <tr>
                  <th>map_key</th>
                  <th>map_label</th>
                  <th>map_value</th>
                  <th>order</th>
                  <th>is_default</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {maps.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center", padding: 16 }}>
                      Aucune entree de map.
                    </td>
                  </tr>
                )}
                {maps.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <input
                        type="text"
                        className="form-input"
                        value={row.map_key}
                        onChange={(e) =>
                          updateMapRow(row.id, "map_key", e.target.value)
                        }
                        placeholder="ex: status_map"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-input"
                        value={row.map_label}
                        onChange={(e) =>
                          updateMapRow(row.id, "map_label", e.target.value)
                        }
                        placeholder="Libelle affiche"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-input"
                        value={row.map_value}
                        onChange={(e) =>
                          updateMapRow(row.id, "map_value", e.target.value)
                        }
                        placeholder="Valeur stockee"
                        style={{
                          width: `${Math.min(
                            Math.max((row.map_value?.length || 0) + 1, 8),
                            80
                          )}ch`,
                          maxWidth: "100%",
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-input"
                        value={
                          row.order === null || row.order === undefined
                            ? ""
                            : row.order
                        }
                        style={{ minWidth: 120 }}
                        onChange={(e) =>
                          updateMapRow(row.id, "order", e.target.value)
                        }
                      />
                    </td>
                    <td style={{ width: 130, textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={!!row.is_default}
                        onChange={(e) =>
                          updateMapRow(row.id, "is_default", e.target.checked)
                        }
                      />
                    </td>
                    <td className="actions">
                      <div
                        className="action-btn"
                        title="Supprimer"
                        onClick={() => deleteMapRow(row.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="config-section">
        <div className="config-body">
          <div className="page-actions">
            <button
              className="btn btn-outline"
              onClick={() => (onBack ? onBack() : window.history.back())}
              disabled={isSaving}
            >
              <i className="fas fa-times"></i> Annuler
            </button>
            <button
              className="btn btn-success"
              onClick={() => {
                console.log("[PlaceholdersConfig] CLICK Appliquer");
                saveConfiguration();
              }}
              disabled={isSaving}
            >
              <i className="fas fa-check"></i>{" "}
              {isSaving ? "Application..." : "Appliquer les modifications"}
            </button>
          </div>
        </div>
      </div>

      {showExportPicker && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: 2000,
            background: "white",
            border: "1px solid #e5e7eb",
            boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
            borderRadius: 12,
            padding: 16,
            minWidth: 280,
          }}
          role="dialog"
          aria-modal="true"
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Exporter la configuration
          </div>
          <div style={{ fontSize: 13, color: "#4b5563", marginBottom: 12 }}>
            Choisis le format de sortie :
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className="btn btn-outline"
              onClick={() => {
                exportConfigAsJSON();
                setShowExportPicker(false);
              }}
            >
              <i className="fas fa-file-code" /> JSON
            </button>
            <button
              className="btn btn-outline"
              onClick={() => {
                exportConfigAsExcel();
                setShowExportPicker(false);
              }}
            >
              <i className="fas fa-file-excel" /> Excel (.xlsx)
            </button>
            <button
              className="btn"
              onClick={() => setShowExportPicker(false)}
              style={{ marginLeft: "auto" }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {showFormulaHelper && (
        <FormulaHelper
          isOpen={showFormulaHelper}
          onClose={() => setShowFormulaHelper(false)}
          onInsertFunction={insertFormulaFunction}
          fields={fields}
        />
      )}
    </div>
  );
};

export default PlaceholdersConfig;
